import { AppAvatar, AppUserAvatar, UserAvatar } from 'components/core/avatar'
import {
    AccessReviewStateFacetIconOpen,
    AccessReviewStateFacetIconPending,
    AccessReviewStateFacetIconUnspecified,
    AccessReviewStateFacetIconClosed,
} from 'components/pages/accessreview/status/icons'
import {
    AppUserAccountTypeServiceUserIcon,
    AppUserAccountTypeSystemUserIcon,
    AppUserAccountTypeUserIcon,
} from 'components/pages/app/users/account_type/icons'
import {
    AppUserStatusIconDeleted,
    AppUserStatusIconDisabled,
    AppUserStatusIconEnabled,
    AppUserStatusIconUnknown,
} from 'components/pages/app/users/status/icons'
import { ResourceTypeAvatar } from 'components/pages/entitlement/resource-type/icons'
import {
    UserStatusIconDeleted,
    UserStatusIconDisabled,
    UserStatusIconEnabled,
    UserStatusIconUnknown,
} from 'components/pages/user/user_status/icons'
import React from 'react'

type FacetIconProps = {
    iconUrl: string
    className?: string
    iconSize?: 'small' | 'inherit' | 'medium' | 'large'
    objectID?: string
}

// FIXME(morgabra) This doesn't feel right.
const FacetIcon = ({ iconUrl, className, iconSize, objectID }: FacetIconProps): React.ReactElement | null => {
    if (iconUrl.startsWith('icon:app-user-type')) {
        return <AppUserTypeFacetIcon iconUrl={iconUrl} className={className} iconSize={iconSize} />
    }
    if (iconUrl.startsWith('icon:user:')) {
        const user = { displayName: iconUrl.slice(10) }
        return <UserAvatar user={user} readOnly />
    }
    if (iconUrl.startsWith('icon:app-user:')) {
        return <AppUserAvatar displayName={iconUrl.slice(14)} />
    }
    if (iconUrl.startsWith('icon:app-user-status')) {
        return <AppUserStatusFacetIcon iconUrl={iconUrl} className={className} iconSize={iconSize} />
    }
    if (iconUrl.startsWith('icon:user-status')) {
        return <UserStatusFacetIcon iconUrl={iconUrl} className={className} iconSize={iconSize} />
    }
    if (iconUrl.startsWith('icon:entitlement-resource-type')) {
        return <EntitlementResourceTypeFacetIcon iconUrl={iconUrl} className={className} iconSize={iconSize} />
    }
    if (iconUrl.startsWith('icon:access-review-state:')) {
        return <AccessReviewStateFacetIcon iconUrl={iconUrl} className={className} iconSize={iconSize} />
    }
    if (iconUrl.startsWith('icon:appname')) {
        return <AppTypeFacetIcon objectID={objectID} iconUrl={iconUrl} className={className} iconSize={iconSize} />
    }

    return null
}

const AccessReviewStateFacetIcon = ({ iconUrl, className, iconSize }: FacetIconProps): React.ReactElement | null => {
    switch (iconUrl) {
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_OPEN':
            return <AccessReviewStateFacetIconOpen className={className} iconSize={iconSize} />
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_DRAFT':
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_PENDING':
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_REVIEW':
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_PREPARING':
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_STARTING':
            return <AccessReviewStateFacetIconPending className={className} iconSize={iconSize} />
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_UNSPECIFIED':
            return <AccessReviewStateFacetIconUnspecified className={className} iconSize={iconSize} />
        case 'icon:access-review-state:ACCESS_REVIEW_STATE_CLOSED':
            return <AccessReviewStateFacetIconClosed className={className} iconSize={iconSize} />
        default:
            return null
    }
}

const AppUserTypeFacetIcon = ({ iconUrl, className, iconSize }: FacetIconProps): React.ReactElement | null => {
    switch (iconUrl) {
        case 'icon:app-user-type:APP_USER_TYPE_USER':
            return <AppUserAccountTypeUserIcon className={className} iconSize={iconSize} />
        case 'icon:app-user-type:APP_USER_TYPE_SERVICE_ACCOUNT':
            return <AppUserAccountTypeServiceUserIcon className={className} iconSize={iconSize} />
        case 'icon:app-user-type:APP_USER_TYPE_SYSTEM_ACCOUNT':
            return <AppUserAccountTypeSystemUserIcon className={className} iconSize={iconSize} />
        default:
            return null
    }
}

const AppUserStatusFacetIcon = ({ iconUrl, className, iconSize }: FacetIconProps): React.ReactElement | null => {
    switch (iconUrl) {
        case 'icon:app-user-status:STATUS_ENABLED':
            return <AppUserStatusIconEnabled className={className} iconSize={iconSize} />
        case 'icon:app-user-status:STATUS_DISABLED':
            return <AppUserStatusIconDisabled className={className} iconSize={iconSize} />
        case 'icon:app-user-status:STATUS_DELETED':
            return <AppUserStatusIconDeleted className={className} iconSize={iconSize} />
        case 'icon:app-user-status:STATUS_UNSPECIFIED':
            return <AppUserStatusIconUnknown className={className} iconSize={iconSize} />
        default:
            return null
    }
}

const EntitlementResourceTypeFacetIcon = ({ iconUrl }: FacetIconProps): React.ReactElement | null => {
    const iconParts = iconUrl.split(':')
    if (iconParts.length !== 3) {
        return null
    }
    return <ResourceTypeAvatar resourceTypeName={iconParts[2]} />
}

const UserStatusFacetIcon = ({ iconUrl, className, iconSize }: FacetIconProps): React.ReactElement | null => {
    switch (iconUrl) {
        case 'icon:user-status:ENABLED':
            return <UserStatusIconEnabled className={className} iconSize={iconSize} />
        case 'icon:user-status:DISABLED':
            return <UserStatusIconDisabled className={className} iconSize={iconSize} />
        case 'icon:user-status:DELETED':
            return <UserStatusIconDeleted className={className} iconSize={iconSize} />
        case 'icon:user-status:UNKNOWN':
            return <UserStatusIconUnknown className={className} iconSize={iconSize} />
        default:
            return null
    }
}

const AppTypeFacetIcon = ({ iconUrl, objectID }: FacetIconProps): React.ReactElement | null => {
    const iconParts = iconUrl.split(':')
    if (iconParts.length !== 3) {
        return null
    }

    return <AppAvatar appId={objectID} appName={iconParts[2]} size="xs" />
}

export default FacetIcon
